import { useRef } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useAiCreditsBalance, type AiCreditsBalance } from "@/hooks/useAiCreditsBalance";
import { formatQuotaResetDate } from "@/lib/ai/quotaReset";

/**
 * Warn while there is still runway, in ABSOLUTE credits.
 *
 * A pure percentage is the wrong unit at both ends of the plan range: 20% of a
 * PROFESSIONAL cap is 500 credits (months of noise), while 20% of the FREE
 * cap is 10 credits — and a turn in the event-create wizard bills SEVERAL
 * credits, so those 10 are a handful of messages. The absolute floor rescues
 * small caps; the ratio (inherited from the balance hook, and the same 80%-of-
 * cap line the backend's warning mail uses) keeps large plans from warning
 * only at the very last moment.
 */
// Stays BELOW the smallest plan cap (FREE = 50): a floor above it would mark
// every FREE company "low" from its very first turn.
const LOW_REMAINING = 30;

/**
 * Last-stretch band: no dismiss, escalated tone.
 *
 * Sized in MESSAGES, not credits. One message bills up to
 * `nikita.ai.max-billed-calls-per-message` model calls at the lane price —
 * eighteen credits on the event-wizard lane — so the previous nine left the
 * user less than a single expensive message of runway and the escalation
 * arrived after it was already too late to act on.
 */
const CRITICAL_REMAINING = 18;

export type AiCreditsRung = "ok" | "low" | "critical" | "empty";

/**
 * Did the server refuse the most recent turn for want of credits?
 *
 * Scans back to the last ASSISTANT message and ignores anything after it.
 * Reading `messages[last]` instead put the refusal on a hair trigger: the
 * moment the user typed again, the trailing user message cleared it and the
 * ring flipped back to "you have credits" — mid-way through the request that
 * was about to be refused for the very same reason.
 *
 * A later successful assistant turn does clear it, which is the intended
 * recovery signal.
 */
export function hasQuotaRejection(
  messages: { role: string; errorCode?: string }[],
): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role !== "assistant") continue;
    return messages[i].errorCode === "aiQuotaExceeded";
  }
  return false;
}

export interface AiCreditsStatus extends AiCreditsBalance {
  rung: AiCreditsRung;
  /** True once the server has actually refused a turn this conversation. */
  rejected: boolean;
  /**
   * Ready-to-render sentence naming the reset date, or null when no date is
   * known. Null is NOT "does not renew" — see the derivation below.
   */
  resetLine: string | null;
}

/**
 * The credit balance plus the escalation rung the UI renders from.
 *
 * Deliberately composes {@link useAiCreditsBalance} instead of opening its own
 * query: hosts (and their tests) mock that one module, and a second query here
 * would need a QueryClientProvider in every harness that renders a Velo panel.
 *
 * `rejected` is the caller's knowledge that the last assistant turn came back
 * with `aiQuotaExceeded`, and it OVERRIDES the counters. That is not
 * belt-and-braces — the counters genuinely disagree with the server near the
 * end: the spend guard is all-or-nothing (`used + turnCost <= cap`), so a
 * surface that bills 3 credits per turn is already refusing every message
 * while the raw balance still reads "2 of 50 left". Once the server has said
 * no, the UI says empty.
 */
export function useAiCreditsStatus(opts?: { rejected?: boolean }): AiCreditsStatus {
  const t = useTranslations("aiAssistant");
  const locale = useLocale();
  const balance = useAiCreditsBalance();

  // A refusal describes the balance AS IT WAS. Remember what was left at that
  // moment: once the balance grows past it the company has topped up, and
  // holding on to the refusal would keep the ring red — and the corrective
  // notice suppressed — on a wallet the user just refilled.
  const remainingAtRejection = useRef<number | null>(null);
  if (!opts?.rejected) {
    remainingAtRejection.current = null;
  } else if (remainingAtRejection.current === null) {
    remainingAtRejection.current = balance.remaining;
  }
  const rejected =
    !!opts?.rejected &&
    (remainingAtRejection.current === null ||
      balance.remaining <= remainingAtRejection.current);

  if (!balance.loaded) return { ...balance, rung: "ok", rejected, resetLine: null };

  // Three states, and the third one stays SILENT.
  //
  // A missing date on its own has four possible meanings (lifetime allowance ·
  // not stamped yet · older backend · caller did not ask), and this surface
  // cannot tell them apart — the plan type is only readable through the
  // subscription endpoint, which is OWNER-gated while the panels are open to
  // EDITORs. So the negative sentence needs its own evidence: `quotaRenews`
  // is written by the very same pipeline that writes the date, and only an
  // explicit `false` licenses "does not renew". `undefined` is not a no.
  //
  // Rendered as a date, never a countdown: the roll is evaluated once a day.
  const resetLine = balance.resetAt
    ? t("creditsRenewOn", { date: formatQuotaResetDate(balance.resetAt, locale) })
    : balance.quotaRenews === false
      ? t("creditsNoRenew")
      : null;

  const empty = balance.exhausted || rejected;
  const critical = !empty && balance.remaining <= CRITICAL_REMAINING;
  const low = !empty && !critical && (balance.low || balance.remaining <= LOW_REMAINING);

  const rung: AiCreditsRung = empty ? "empty" : critical ? "critical" : low ? "low" : "ok";

  return {
    ...balance,
    // Keep the derived flags in step with the rung so the ring (which reads
    // `low`/`exhausted`) can never render green next to a red strip.
    low: low || critical,
    exhausted: empty,
    rung,
    rejected,
    resetLine,
  };
}
